import { fromPairs, isUndefined, mapValues, omitBy } from "lodash";
import { BASE_OPTION_NAME, NAME_SEPARATOR } from "./constant";
import {
  ConfigNode,
  ConfigOption,
  DependencyGroupValue,
  NodeRefFields,
  OptionNameParts,
  RefConverter,
  RefEntry,
} from "./types";

const SHARED_BUCKET_URL =
  /^https:\/\/adh-online-serverless-storage-(dev|stg|prd)\.s3\.ap-southeast-2\.amazonaws\.com\//;

export class HomeConfigUtil {
  static assetEnv(url: string): string | undefined {
    return url.match(SHARED_BUCKET_URL)?.[1];
  }

  static parseOptions(text: string): ConfigOption[] {
    const parsed: unknown = JSON.parse(text);
    if (!Array.isArray(parsed)) {
      throw new Error("Config file must be a JSON array of options");
    }
    return parsed;
  }

  static splitName(name: string): OptionNameParts {
    const lastIndex = name.lastIndexOf(NAME_SEPARATOR);
    const leaf = name.substring(lastIndex + NAME_SEPARATOR.length);
    if (lastIndex > 0) {
      return { group: name.substring(0, lastIndex), leaf };
    }
    return { group: name, leaf };
  }

  static isBaseOption(option: ConfigOption): boolean {
    return option.name.trim().toLowerCase() === BASE_OPTION_NAME;
  }

  static getGroups(options: ConfigOption[]) {
    const result: Record<string, string[]> = {};
    options.map((item) => {
      // NOTE: the base config's map node is not a pickable option, so it must
      // never surface as a customGroup entry.
      if (HomeConfigUtil.isBaseOption(item)) {
        return;
      }
      const { group, leaf } = HomeConfigUtil.splitName(item.name);
      if (Object.keys(result).includes(group) === false) {
        result[group] = [leaf];
      } else {
        result[group].push(leaf);
      }
    });
    return result;
  }

  // What a node's filterByOption / tagsOption may point at. A list-row row is its
  // own toggle, so it is named by its option path — its group holds several
  // independent rows and would be ambiguous.
  static refPaths(options: ConfigOption[]): string[] {
    const rows = options
      .filter((option) => option.presentation?.layout === "list-row")
      .map((option) => option.name);
    return [...Object.keys(HomeConfigUtil.getGroups(options)), ...rows];
  }

  // NOTE: Refs are stored as option ids so a rename never touches them; the option
  // editor works on names, so an option is turned to names when opened and back
  // to ids when committed. Ids are minted here so a ref is an id from the first save.
  static toIdRefs(option: ConfigOption, list: ConfigOption[]): ConfigOption {
    const byName = new Map(
      list.map((entry): RefEntry<ConfigOption> => [entry.name, entry]),
    );
    const groupId = (value: string): string => {
      return HomeConfigUtil.groupAnchor(list, value)?.id ?? value;
    };
    return HomeConfigUtil.mapRefs(option, {
      ref: (value) => {
        const target = byName.get(value);
        if (target) {
          return HomeConfigUtil.isListRow(target) && target.id
            ? target.id
            : value;
        }
        return groupId(value);
      },
      group: groupId,
      pair: (group, leaf) => {
        const target = byName.get(`${group}${NAME_SEPARATOR}${leaf}`);
        return [target?.id ?? group, leaf];
      },
    });
  }

  static toNameRefs(option: ConfigOption, list: ConfigOption[]): ConfigOption {
    const byId = new Map(
      list
        .filter((entry) => entry.id)
        .map((entry): RefEntry<ConfigOption> => [entry.id, entry]),
    );
    const groupOf = (value: string): string => {
      const target = byId.get(value);
      return target ? HomeConfigUtil.splitName(target.name).group : value;
    };

    return HomeConfigUtil.mapRefs(option, {
      ref: (value) => {
        const target = byId.get(value);
        const isRow = target && HomeConfigUtil.isListRow(target);
        return isRow ? target.name : groupOf(value);
      },
      group: groupOf,
      pair: (group, leaf) => {
        const target = byId.get(group);
        if (!target) {
          return [group, leaf];
        }
        const parts = HomeConfigUtil.splitName(target.name);
        return [parts.group, parts.leaf];
      },
    });
  }

  // NOTE: Only a base config mints ids. A child option the parent owns must carry the
  // parent's id, which the backend fills on save — a minted one would differ and pin
  // the option as a child override, so a child keeps name refs until the next load.
  static refsToIds(options: ConfigOption[], mintIds: boolean): ConfigOption[] {
    const list = mintIds
      ? options.map((option) => {
          return option.id ? option : { ...option, id: crypto.randomUUID() };
        })
      : options;
    return list.map((option) => {
      return HomeConfigUtil.toIdRefs(option, list);
    });
  }

  // NOTE: A group ref anchors on one member's id, so deleting that member would
  // orphan a group that is still alive — hand the ref to a remaining member.
  static reanchorRefs(
    options: ConfigOption[],
    removed: ConfigOption,
  ): ConfigOption[] {
    const { group } = HomeConfigUtil.splitName(removed.name);
    const sibling = HomeConfigUtil.groupAnchor(options, group);
    if (!removed.id || !sibling) {
      return options;
    }
    const swap = (value: string): string => {
      return value === removed.id ? sibling.id : value;
    };
    return options.map((option) => {
      return HomeConfigUtil.mapRefs(option, {
        ref: swap,
        group: swap,
        pair: (key, leaf) => [key, leaf],
      });
    });
  }

  private static isListRow(option: ConfigOption): boolean {
    return option.presentation?.layout === "list-row";
  }

  // NOTE: A list-row option resolves as its own row, never as its group, so only
  // a non-row member can stand for the group.
  private static groupAnchor(
    options: ConfigOption[],
    group: string,
  ): ConfigOption | undefined {
    return options.find((option) => {
      const isMember = HomeConfigUtil.splitName(option.name).group === group;
      return option.id && isMember && !HomeConfigUtil.isListRow(option);
    });
  }

  private static mapRefs(
    option: ConfigOption,
    convert: RefConverter,
  ): ConfigOption {
    const mapNode = (node: ConfigNode): ConfigNode => {
      const refs: NodeRefFields = node;
      const mapped = {
        tagsOption: refs.tagsOption && mapValues(refs.tagsOption, convert.ref),
        filterByOption:
          refs.filterByOption && mapValues(refs.filterByOption, convert.ref),
        options:
          refs.options &&
          fromPairs(
            Object.entries(refs.options).map(([group, leaf]) => {
              return convert.pair(group, leaf);
            }),
          ),
        dependencyGroup:
          refs.dependencyGroup &&
          fromPairs(
            Object.entries(refs.dependencyGroup).map(
              ([group, condition]): RefEntry<DependencyGroupValue> => {
                const [key, leaf] = convert.pair(group, condition.value);
                return [key, { ...condition, value: leaf }];
              },
            ),
          ),
      };
      return { ...node, ...omitBy(mapped, isUndefined) };
    };
    const triggers = option.dependency?.triggers?.map(convert.group);
    return {
      ...option,
      dependency: option.dependency && { ...option.dependency, triggers },
      nodes: option.nodes?.map(mapNode),
      uncheckedNodes: option.uncheckedNodes?.map(mapNode),
    };
  }
}
